define((require) => {
    "use strict";

    const {isNumber, isUndefined, first}    = require("underscore");
    const Backbone                          = require("backbone");
    const {isBlank}                         = require("shared/util/StringUtils");
    const FontDescriptor                    = require("shared/data/fonts/FontDescriptor");
    const Preconditions                     = require("shared/util/Preconditions");
    const FontProvider                      = require("shared/data/fonts/FontProvider");

    /**
     * The default maximum number of search results per font provider.
     *
     * @type {Number}
     */
    const DEFAULT_SEARCH_LIMIT = 20;
    const DEFAULT_TAKE_AMOUNT = 20;

    /**
     * Convenience collection for working with fonts.
     *
     * The collection is used to store font descriptors from various font providers, e.g. Google, Typekit, standard
     * web fonts etc. - providing a common interface.
     *
     * This should only be used from within the "editor".
     *
     * @see EditorWebFontController
     *
     * @class FontDescriptorCollection
     * @author Kim
     */
    return Backbone.Collection.extend({

        model: FontDescriptor,

        /**
         * Returns an array of <tt>FontDescriptor</tt>'s from the specified <tt>FontProvider</tt>.
         *
         * @param  {FontProvider} fontProvider The <tt>FontProvider</tt>.
         * @return {FontDescriptor[]}          Array of <tt>FontDescriptor</tt>'s.
         */
        fromProvider(fontProvider) {
            return this.filter((fontDescriptor) => fontDescriptor.get("provider") === fontProvider);
        },

        /**
         * Removes all the <tt>FontDescriptor</tt>'s (if any) that belongs to the specified <tt>FontProvider</tt>.
         *
         * @param  {FontProvider} fontProvider The font provider.
         */
        removeByProvider(fontProvider) {
            this.remove(this.fromProvider(fontProvider));
        },

        /**
         * Returns the contained <tt>FontDescriptor</tt> from the specified font family and font provider.
         *
         * @param  {String} fontFamily   The font family, e.g. <tt>Arial</tt> (case insensitive).
         * @param  {FontProvider} fontProvider The font provider.
         * @return {FontDescriptor}      The matching font provider, if any.
         */
        fromFamily(fontFamily, fontProvider) {
            Preconditions.isString(fontFamily);
            Preconditions.instanceOf(fontProvider, FontProvider);

            return this.find((fontDescriptor) =>
                fontDescriptor.get("family").toLowerCase() === fontFamily.toLowerCase() &&
                    fontDescriptor.get("provider") === fontProvider);
        },

        /**
         * Adds a new <tt>FontDescriptor</tt> to the collection. If a descriptor with the same font family
         * (and provider) already exists, the existing provider gets updated.
         *
         * @param {String} family The font family.
         * @param {String} variation  The variation, i.e. font style/weight.
         * @param {FontProvider} provider  The <tt>FontProvider</tt>.
         * @param {Object} data Optional font descriptor data.
         */
        addDescriptor(family, variation, provider, data) {
            const descriptor = this.fromFamily(family, provider);

            if (descriptor && descriptor.get("variations").indexOf(variation) === -1) {
                descriptor.get("variations").push(variation);
            } else {
                const variations = [variation];
                this.add(new FontDescriptor({
                    family,
                    provider,
                    variations,
                    data
                }));
            }
        },

        /**
         * Performs a basic text search from the specified search <tt>term</tt>, matching any contained font family.
         *
         * @param  {String|Number} term  The term to search for as a string value, or a numeric limit value. If a
         *                               number (or undefined) is used, a map of all font providers are returned, with
         *                               the limited number of search results.
         *
         * @param  {Number} limit        The maximum number of returned search results per font
         *                               provider. Defaults to <tt>DEFAULT_SEARCH_LIMIT</tt>.
         *
         * @return {Map.<FontProvider, FontDescriptor[]>} A map of search results, where <tt>FontDescriptor</tt>'s are
         *                                                mapped to their <tt>FontProvider</tt>.
         */
        search(term, limit) {
            const results = new Map();

            if (isNumber(term)) {
                limit = term;
                term = "";
            } else if (isUndefined(term)) {
                term = "";
            }

            FontProvider.all().forEach((provider) => {
                const providerFonts = this.fromProvider(provider);
                const isUnlimited = provider === FontProvider.CUSTOM;
                if (providerFonts.length > 0) {
                    const providerResults = isBlank(term) ? providerFonts :
                        providerFonts.filter((fontDescriptor) => {
                            const family = fontDescriptor.get("family").toLowerCase();
                            return family.indexOf(term.toLowerCase()) !== -1;
                        });

                    if (providerResults.length > 0) {
                        const fontResults = isUnlimited ?
                            providerResults : first(providerResults, limit || DEFAULT_SEARCH_LIMIT);
                        results.set(provider, fontResults);
                    }
                }
            });

            return results;
        },

        /**
         * Takes the first `amount` font descriptors of the collection.
         *
         * @param  {Number} amount      The number of returned items. Defaults
         *                              to DEFAULT_TAKE_AMOUNT.
         *
         * @return {Map.<FontProvider, FontDescriptor[]>}
         *                              A map of search results, where
         *                              <tt>FontDescriptor</tt>'s are mapped to
         *                              their <tt>FontProvider</tt>.
         */
        take(amount) {
            amount = isNumber(amount) ? amount : DEFAULT_TAKE_AMOUNT;

            return this.search("", amount);
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/data/fonts/FontDescriptorCollection.js